import React, { useState, useEffect } from 'react';
import PageLoader from '../components/PageLoader';
import { api } from '../App';
import '../styles/AdminCummulatieveLijstPage.css';
import { exportCummulatieveLijstPdf } from '../utils/exportOrderListPdf';

function AdminCummulatieveLijstPage({ user, onBack, onLogout }) {
  const [datums, setDatums] = useState([]);
  const [selectedDatum, setSelectedDatum] = useState('');
  const [lijst, setLijst] = useState(null);
  const [loadingDatums, setLoadingDatums] = useState(true);
  const [loadingLijst, setLoadingLijst] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDatums();
  }, []);

  const loadDatums = async () => {
    try {
      const response = await api.get('/index.php?action=admindatums');
      setDatums(response.data);
      setLoadingDatums(false);
    } catch (err) {
      setError('Fout bij laden van datums');
      setLoadingDatums(false);
    }
  };

  const loadLijst = async (datumId) => {
    if (!datumId) {
      setLijst(null);
      return;
    }

    try {
      setLoadingLijst(true);
      setError('');
      const response = await api.get(`/index.php?action=cummulatiefelijst&datum_id=${datumId}`);
      setLijst(response.data);
      setLoadingLijst(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij laden van boodschappenlijst');
      setLijst(null);
      setLoadingLijst(false);
    }
  };

  const handleDatumChange = (e) => {
    const datumId = e.target.value;
    setSelectedDatum(datumId);
    loadLijst(datumId);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPdf = () => {
    if (!lijst || (!hasItems && !hasNotities)) return;
    exportCummulatieveLijstPdf(lijst);
  };

  const regels = lijst?.regels || [];
  const vrijeRegels = lijst?.vrije_regels || [];
  const notitiesPerSpeltak = lijst?.notities_per_speltak || [];
  const hasItems = regels.length > 0 || vrijeRegels.length > 0;
  const hasNotities = notitiesPerSpeltak.length > 0;
  const canExport = hasItems || hasNotities;

  if (loadingDatums) {
    return <PageLoader />;
  }

  return (
    <div className="admin-cummulatief-container">
      <header className="admin-header no-print">
        <button onClick={onBack} className="back-button">← Terug</button>
        <h1>Cumulatieve boodschappenlijst</h1>
        <div className="header-actions">
          {lijst && canExport && (
            <>
              <button type="button" onClick={handleExportPdf} className="export-pdf-button">
                PDF export
              </button>
              <button type="button" onClick={handlePrint} className="print-button">🖨️ Print</button>
            </>
          )}
          <button onClick={onLogout} className="logout-button">Uitloggen</button>
        </div>
      </header>

      <main className="admin-main">
        <div className="cummulatief-wrapper">
          <div className="datum-selector no-print">
            <label htmlFor="leverdatum-select">
              <strong>Selecteer leveringsdatum</strong>
            </label>
            <select
              id="leverdatum-select"
              value={selectedDatum}
              onChange={handleDatumChange}
              className="datum-select"
            >
              <option value="">-- Kies een datum --</option>
              {datums.map((datum) => (
                <option key={datum.ID} value={datum.ID}>
                  {datum.formatted}
                </option>
              ))}
            </select>
          </div>

          {error && <div className="error-message no-print">{error}</div>}

          {loadingLijst && (
            <p className="loading-text no-print">Boodschappenlijst laden...</p>
          )}

          {selectedDatum && !loadingLijst && lijst && canExport && (
            <div className="printable-area">
              <div className="lijst-header">
                <h2>Kampboodschappen – cumulatieve lijst</h2>
                <div className="lijst-meta">
                  <p><strong>Leverdatum:</strong> {lijst.leverdatum}</p>
                  <p><strong>Aantal bestellingen:</strong> {lijst.aantal_bestellingen}</p>
                </div>
              </div>

              {!hasItems ? (
                <p className="no-items">Geen producten gevonden voor deze datum.</p>
              ) : (
                <table className="lijst-table">
                  <thead>
                    <tr>
                      <th>Hoeveelheid</th>
                      <th>Product</th>
                      <th>Eenheid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regels.map((regel, index) => (
                      <tr key={`regel-${index}`}>
                        <td className="quantity">{regel.hoeveelheid}</td>
                        <td>{regel.product}</td>
                        <td>{regel.eenheid || '-'}</td>
                      </tr>
                    ))}
                    {vrijeRegels.map((regel, index) => (
                      <tr key={`vrij-${index}`}>
                        <td className="quantity">{regel.hoeveelheid}</td>
                        <td>{regel.product} <span className="anders-label">(Anders)</span></td>
                        <td>-</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {hasNotities && (
                <div className="notities-print-page">
                  <div className="notities-page-header">
                    <h2>Notities per speltak</h2>
                    <div className="lijst-meta">
                      <p><strong>Leverdatum:</strong> {lijst.leverdatum}</p>
                      <p><strong>Aantal notities:</strong> {lijst.aantal_notities}</p>
                    </div>
                  </div>

                  <div className="notities-speltak-lijst">
                    {notitiesPerSpeltak.map((speltak) => (
                      <section key={speltak.speltak_id} className="notities-speltak-blok">
                        <h3 className="notities-speltak-titel">{speltak.speltak_naam}</h3>
                        {speltak.notities.map((item) => (
                          <div key={`notitie-${item.bestelling_id}`} className="notitie-item">
                            <p className="notitie-item-meta">
                              Bestelling #{item.bestelling_id} · besteld op {item.besteldatum}
                            </p>
                            <p className="notitie-item-tekst">{item.notitie}</p>
                          </div>
                        ))}
                      </section>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedDatum && !loadingLijst && lijst && !hasItems && !hasNotities && (
            <p className="no-items no-print">Geen bestellingen gevonden voor deze datum.</p>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminCummulatieveLijstPage;
